import { ArrowUpRight, Store, ShieldCheck, RefreshCw, BookOpen, Sparkles } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Suspense } from "react";
import { fyndanuListingsQuery } from "@/components/FyndanuGrid";

const STORE_URL =
  "https://www.fyndanu.se/butik/e435da0b-10d0-46fa-a389-702295c89840";

function LiveCount() {
  const { data } = useSuspenseQuery(fyndanuListingsQuery);
  const n = data.items.length;
  if (!n) return <span className="tabular-nums">flera</span>;
  return <span className="tabular-nums">{n}</span>;
}

/**
 * Full-width partner strip. Positions AK as the official storefront on
 * Fyndanu — used on the home page and on /fordon.
 */
export function FyndanuPartnerStrip() {
  return (
    <section
      aria-label="Officiell partner till Fyndanu.se"
      className="relative overflow-hidden border-y border-border bg-foreground text-background"
    >
      {/* soft accent glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-60"
        style={{
          background:
            "radial-gradient(70% 90% at 15% 30%, oklch(0.45 0.14 38 / 0.35), transparent 65%), radial-gradient(50% 70% at 85% 70%, oklch(0.35 0.08 60 / 0.35), transparent 65%)",
        }}
      />
      {/* subtle grain */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            "radial-gradient(rgba(255,255,255,0.6) 1px, transparent 1px)",
          backgroundSize: "3px 3px",
        }}
      />

      <div className="container-edge relative py-14 md:py-20">
        <div className="grid gap-10 md:grid-cols-12 md:items-center">
          <div className="md:col-span-7">
            <span className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/15 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.22em] text-accent">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent opacity-70" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-accent" />
              </span>
              Officiell partner
            </span>

            <h2 className="mt-5 font-display text-4xl leading-[0.95] md:text-6xl">
              Vi listar hela lagret på{" "}
              <a
                href={STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="font-italic-display headline-accent underline decoration-accent/40 decoration-2 underline-offset-[6px] hover:decoration-accent"
              >
                Fyndanu.se
              </a>
            </h2>

            <p className="mt-6 max-w-xl text-base leading-relaxed text-background/75 md:text-lg">
              AK Husbil & Fritid är officiell butik på Fyndanu — den nya
              svenska marknadsplatsen för husbilar och husvagnar. Varje
              fordon vi säljer publiceras direkt där, med kompletta bilder,
              specifikationer och pris. Klart, tryggt och alltid uppdaterat.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-cta btn-cta-accent group inline-flex items-center gap-2 px-7 py-3.5 text-sm font-semibold"
              >
                <Store size={16} /> Öppna vår Fyndanu-butik
                <ArrowUpRight
                  size={16}
                  className="transition group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                />
              </a>
              <a
                href="https://www.fyndanu.se/salj-husbil"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-cta btn-cta-glass inline-flex items-center gap-2 px-7 py-3.5 text-sm font-semibold"
              >
                Sälj din husbil via oss
              </a>
            </div>

            {/* Elaborate blog CTA — leads to the deep-dive post about our Fyndanu partnership */}
            <div className="mt-8 max-w-xl">
              <Link
                to="/blogg/$slug"
                params={{ slug: "fyndanu-partner-trygg-husbilsaffar-online-2026" }}
                className="group relative block overflow-hidden rounded-2xl border border-accent/40 bg-gradient-to-br from-accent/15 via-background/[0.04] to-transparent p-[1px] transition hover:border-accent/70"
              >
                {/* animated conic sheen */}
                <span
                  aria-hidden
                  className="pointer-events-none absolute -inset-[100%] opacity-0 transition-opacity duration-500 group-hover:opacity-100"
                  style={{
                    background:
                      "conic-gradient(from 0deg at 50% 50%, transparent 0deg, oklch(0.75 0.15 55 / 0.35) 60deg, transparent 120deg, transparent 240deg, oklch(0.75 0.15 55 / 0.25) 300deg, transparent 360deg)",
                    animation: "spin 6s linear infinite",
                  }}
                />
                <div className="relative flex items-center gap-4 rounded-[15px] bg-foreground/95 px-5 py-4 backdrop-blur">
                  <span className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-accent/15 ring-1 ring-accent/40">
                    <BookOpen size={18} className="text-accent" />
                    <Sparkles size={10} className="absolute -right-0.5 -top-0.5 text-accent animate-pulse" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-accent">
                      Djupdykning · 26 min läsning
                    </p>
                    <p className="mt-1 font-display text-lg leading-tight text-background md:text-xl">
                      Läs guiden: <span className="font-italic-display text-accent">Så tryggt</span> köper du husbil via Fyndanu
                    </p>
                  </div>
                  <span className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent text-background transition group-hover:scale-110">
                    <ArrowUpRight size={18} className="transition group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                    <span aria-hidden className="absolute inset-0 rounded-full bg-accent opacity-60 blur-md transition group-hover:opacity-100" />
                  </span>
                </div>
              </Link>
            </div>
          </div>

          {/* Right column: partner card */}
          <div className="md:col-span-5">
            <div className="relative rounded-3xl border border-background/15 bg-background/[0.04] p-6 backdrop-blur-sm md:p-8">
              <div className="flex items-center justify-between">
                <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-background/60">
                  Marknadsplats
                </p>
                <span className="rounded-full border border-accent/40 bg-accent/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-accent">
                  Verifierad butik
                </span>
              </div>

              {/* Wordmark */}
              <a
                href={STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 block"
              >
                <p className="font-display text-5xl leading-none tracking-tight text-background md:text-6xl">
                  fyndanu
                  <span className="text-accent">.</span>se
                </p>
              </a>
              <p className="mt-3 text-xs text-background/60">
                Nordens transparenta marknadsplats för fritidsfordon.
              </p>

              <div className="mt-6 grid grid-cols-3 gap-3 border-t border-background/15 pt-6">
                <div>
                  <p className="font-display text-2xl text-background">
                    <Suspense fallback={<span className="tabular-nums">–</span>}>
                      <LiveCount />
                    </Suspense>
                  </p>
                  <p className="mt-1 text-[10px] uppercase tracking-[0.18em] text-background/55">
                    Aktiva annonser
                  </p>
                </div>
                <div>
                  <p className="font-display text-2xl text-background">100%</p>
                  <p className="mt-1 text-[10px] uppercase tracking-[0.18em] text-background/55">
                    Vårt lager
                  </p>
                </div>
                <div>
                  <p className="font-display text-2xl text-background">24/7</p>
                  <p className="mt-1 text-[10px] uppercase tracking-[0.18em] text-background/55">
                    Alltid live
                  </p>
                </div>
              </div>

              <ul className="mt-6 space-y-2 text-sm text-background/80">
                <li className="flex items-center gap-2">
                  <ShieldCheck size={14} className="text-accent" /> Kontrollerade fordon — publicerat direkt
                </li>
                <li className="flex items-center gap-2">
                  <RefreshCw size={14} className="text-accent" /> Uppdateras samma dag som lagret ändras
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
